import React from 'react';

import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import theme from '@vromo/ui/components/theme';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';

import Select from 'react-select';

import { css } from 'emotion';

import { Condition, Unit } from './Condition';
import ConditionInput from './components/ConditionInput';
import Flex from '@vromo/ui/components/Flex';
import { qaLabelAutodispatcher } from '../../../../common-qa-labels/autodispatcher';
import { buildDynamicLabel } from '../../../../common-qa-labels/utils';

const closeButton = css`
    color: ${theme.grey};
    cursor: pointer;
    margin-left: 10px;
    transition: color 200ms ease-in;

    &:hover {
        color: red;
    }
`;

interface Props {
    conditionList: Condition[];
    ruleProperties: {label: string, value: string}[];
}

const ConditionEditor = observer<Props>(function ConditionEditor({
    conditionList,
    ruleProperties
}) {
    const { t } = useTranslation();
    const addNewCondition = action(() => {
        conditionList.push(new Condition());
    });

    const editCondition = action((index: number, property?: string | null, value?: string | number | null, unit?: Unit | null) => {
        let newValue = value;
        if (unit) newValue = unit?.multiplier ? Number(value) * unit.multiplier : value
        conditionList[index] = new Condition({ property, value: newValue, unit });
    });

    const removeCondition = action((c: Condition) => {
        if (conditionList.length === 1) { conditionList[0] = new Condition() }
        else {
            const id = conditionList.indexOf(c);
            conditionList.splice(id, 1);
        }
    });

    function availablePropertyOptions(options: {
        label: string;
        value: string;
    }[]) {
        return options.filter((o) => !conditionList.map((c) => c.property).includes(o.value));
    }

    return (
        <>
            <div
                style={{
                    marginBottom: 15
                }}>
                {conditionList.map((c, index) => {
                    const ruleProp = ruleProperties.find((rp) => rp.value === c.property);
                    return (
                        <div key={`${c.property}`} data-testid={qaLabelAutodispatcher.ruleModal.conditionRow}>
                            {index > 0 && <h4>{t("conditionEditor.and")}</h4>}
                            <Flex vertical="middle" horizontal="between">
                                <Select
                                    styles={{
                                        container: (styles) => ({
                                            ...styles,
                                            borderColor: `${theme.lightGrey}`,
                                            flex: 1,
                                            minWidth: '60%',
                                        }),
                                        control: (styles) => ({
                                            ...styles,
                                            borderColor: `${theme.lightGrey}`
                                        }),
                                        indicatorSeparator: (styles) => ({
                                            ...styles,
                                            backgroundColor: 'transparent'
                                        }),
                                        singleValue: (styles) => ({
                                            ...styles,
                                            whiteSpace: 'pre-wrap'
                                        })
                                    }}
                                    value={ruleProp}
                                    onChange={(selected) =>
                                        editCondition(index, selected!.value, undefined, undefined)
                                    }
                                    options={availablePropertyOptions(ruleProperties)}
                                    placeholder="Select a Condition..."
                                    isMulti={false}
                                />
                                {c.type && <ConditionInput
                                    condition={c}
                                    editCondition={editCondition}
                                    index={index}
                                />}
                                <FontAwesome data-testid={buildDynamicLabel(qaLabelAutodispatcher.ruleModal.removeConditionBtn, index)}
                                    className={closeButton}
                                    icon="times"
                                    onClick={() => {
                                        removeCondition(c);
                                    }}
                                    style={{ flex: 'none' }}
                                />
                            </Flex>
                        </div>
                    );
                })}
            </div>
            <Button data-testid={qaLabelAutodispatcher.ruleModal.addConditionBtn}
                disabled={!(conditionList.length < ruleProperties.length)}
                onClick={() => {
                    addNewCondition();
                }}
                style={{ marginRight: 5 }}
            >
                {t("conditionEditor.add")}
            </Button>
        </>
    );
});

export default ConditionEditor;
